import { Bookmark, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useBookmarks, keyOf } from "@/context/BookmarksContext";

// Toggleable save button for a verse/passage or a Strong's word. `payload` must include
// `kind` ("verse" | "word") plus the relevant fields. Two looks: "icon" (compact) and "pill".
export default function BookmarkButton({ payload, variant = "icon", testid = "bookmark-btn", label }) {
  const { isSaved, toggle, busyKey } = useBookmarks();
  const saved = isSaved(payload);
  const busy = busyKey === keyOf(payload);

  const click = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (busy) return;
    try {
      const r = await toggle(payload);
      toast.success(r.saved ? "Saved to your library" : "Removed from your library");
    } catch (err) {
      toast.error("Couldn't update your library");
    }
  };

  const text = label || (payload.kind === "word" ? "Save word" : "Save verse");

  if (variant === "pill") {
    return (
      <button
        type="button"
        data-testid={testid}
        onClick={click}
        title={saved ? "Saved — tap to remove" : text}
        aria-pressed={saved}
        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
          saved
            ? "border-[#00E5FF] bg-[#10222A] text-[#00E5FF]"
            : "border-[#29344D] text-slate-400 hover:border-[#00E5FF]/40 hover:text-[#00E5FF]"
        }`}
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bookmark className={`w-3.5 h-3.5 ${saved ? "fill-current" : ""}`} strokeWidth={1.75} />}
        {saved ? "Saved" : text}
      </button>
    );
  }

  return (
    <button
      type="button"
      data-testid={testid}
      onClick={click}
      title={saved ? "Saved — tap to remove" : text}
      aria-pressed={saved}
      aria-label={text}
      className={`inline-flex items-center justify-center rounded-full p-1.5 transition-colors shrink-0 ${
        saved ? "text-[#00E5FF]" : "text-slate-500 hover:text-[#00E5FF] hover:bg-[#10222A]"
      }`}
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bookmark className={`w-4 h-4 ${saved ? "fill-current" : ""}`} strokeWidth={1.75} />}
    </button>
  );
}
